import type { VacationBalance, VacationRequest } from './vacation.types';
import { CLOSED_REQUEST_STATUSES } from './vacationRequestStatus';

export interface DisplayBalance {
    balanceDays: number;
    expiredDays: number;
    pastLimitDays: number;
    availableDays: number;
    scheduledDays: number;
    nextDeadline: string | null;
    nextLegalDeadline: string | null;
    pendingReconciliation: boolean;
}

export interface PendingBalanceRestore {
    requestId: string;
    days: number;
    removedAtMs: number;
}

export function timestampToMillis(
    value: { toMillis?: () => number; toDate?: () => Date } | null | undefined
): number | null {
    return value?.toMillis?.() ?? value?.toDate?.()?.getTime() ?? null;
}

function hasReservedSell(request: VacationRequest): boolean {
    return (
        request.sellRequest.requested &&
        request.sellRequest.days > 0 &&
        request.sellRequest.status !== 'Rejected'
    );
}

export function resolveReservedDays(request: VacationRequest): number {
    return request.totalDays + (hasReservedSell(request) ? request.sellRequest.days : 0);
}

function sumUnreflectedRestores(
    balance: VacationBalance,
    restores: PendingBalanceRestore[]
): number {
    const calculatedAtMs = timestampToMillis(balance.calculatedAt);
    if (calculatedAtMs === null) return 0;

    const now = Date.now();
    const maxRestoreAge = 2 * 60 * 1000;

    return restores
        .filter(
            (restore) =>
                now - restore.removedAtMs < maxRestoreAge &&
                calculatedAtMs < restore.removedAtMs
        )
        .reduce((sum, restore) => sum + restore.days, 0);
}

export function deriveDisplayBalance(
    balance: VacationBalance | null,
    requests: VacationRequest[],
    restores: PendingBalanceRestore[] = []
): DisplayBalance {
    if (!balance) {
        const derivedScheduled = requests
            .filter((req) => !CLOSED_REQUEST_STATUSES.includes(req.status) && req.status !== 'OnVacation')
            .reduce((sum, req) => sum + req.totalDays, 0);

        return {
            balanceDays: 0,
            expiredDays: 0,
            pastLimitDays: 0,
            availableDays: 0,
            scheduledDays: derivedScheduled,
            nextDeadline: null,
            nextLegalDeadline: null,
            pendingReconciliation: false,
        };
    }

    const derivedScheduled = requests
        .filter((req) => !CLOSED_REQUEST_STATUSES.includes(req.status) && req.status !== 'OnVacation')
        .reduce((sum, req) => sum + req.totalDays, 0);

    const derivedSold = requests
        .filter((req) => !CLOSED_REQUEST_STATUSES.includes(req.status) && hasReservedSell(req))
        .reduce((sum, req) => sum + req.sellRequest.days, 0);

    const backendConsumed =
        balance.scheduledDays +
        balance.pendingDays +
        balance.periods.reduce((sum, period) => sum + period.soldDays + period.pendingSoldDays, 0);

    const totalDerivedConsumption = derivedScheduled + derivedSold;
    const unconsumedDerived = Math.max(0, totalDerivedConsumption - backendConsumed);
    const restoredDays = sumUnreflectedRestores(balance, restores);

    const adjustedBalanceDays = Math.max(0, balance.balanceDays - unconsumedDerived + restoredDays);
    const adjustedExpiredDays = balance.expiredDays;
    const pastLimitDays = balance.pastLimitDays ?? 0;
    const availableDays = Math.max(0, adjustedBalanceDays - adjustedExpiredDays - pastLimitDays);

    return {
        balanceDays: adjustedBalanceDays,
        expiredDays: adjustedExpiredDays,
        pastLimitDays,
        availableDays,
        scheduledDays: Math.max(balance.scheduledDays, derivedScheduled),
        nextDeadline: balance.nextDeadline,
        nextLegalDeadline: balance.nextLegalDeadline ?? null,
        pendingReconciliation: unconsumedDerived > 0 || restoredDays > 0,
    };
}
